import React, { useState } from 'react';
import SelectAllButton from '../../components/SelectAllButton';
import { allergenChecklistData } from './allergenChecklistData';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { FiCheckCircle, FiAlertCircle, FiCamera, FiSquare, FiCheckSquare } from 'react-icons/fi';

export default function AllergenChecklist() {
  // Initialize state with your checklist layout data array
  const [checklist, setChecklist] = useState(allergenChecklistData);

  // Toggle Accordion Collapse state
  const toggleExpand = (id) => {
    setChecklist(prev =>
      prev.map(item => (item.id === id ? { ...item, isExpanded: !item.isExpanded } : item))
    );
  };

  // Toggle individual nested checkbox item
  const toggleSubItemCheck = (sectionId, subItemId) => {
    setChecklist(prev =>
      prev.map(section => {
        if (section.id !== sectionId) return section;
        return {
          ...section,
          subItems: section.subItems.map(subItem =>
            subItem.id === subItemId ? { ...subItem, checked: !subItem.checked } : subItem
          )
        };
      })
    );
  };

  // Toggle Action Button Status ('pass' | 'issue')
  const handleStatusChange = (id, statusType) => {
    setChecklist(prev =>
      prev.map(item => {
        if (item.id !== id) return item;
        // If clicking the active status button, clear it back to null
        const newStatus = item.status === statusType ? null : statusType;
        return { ...item, status: newStatus };
      })
    );
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm font-sans select-none overflow-hidden">
      {/* Top Header Labels */}
      <div className="flex items-center justify-between p-4 bg-gray-50/50 border-b border-gray-100 text-xs font-bold text-gray-500 tracking-wider">
        <span>Allergen Review Items</span>
        <span className="mr-24 hidden md:block">Status</span>
      </div>

      {/* Main List Body */}
      <div className="divide-y divide-gray-100">
        {checklist.map((item) => {
          const SectionIcon = item.icon;
          
          return (
            <div key={item.id} className="p-4 md:p-6 transition hover:bg-gray-50/30">
              
              {/* Header Container Row */}
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                
                {/* Left block: Numeric Icon badge and Title blocks */}
                <div className="flex items-start space-x-4 min-w-0 flex-1">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold relative shadow-md shadow-blue-100">
                    <SectionIcon className="w-5 h-5" />
                    <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white text-blue-600 border border-blue-100 text-[10px] font-black rounded-full flex items-center justify-center">
                      {item.number}
                    </span>
                  </div>

                  <div className="space-y-1 min-w-0">
                    <h3 className="text-sm md:text-base font-bold text-gray-900 truncate text-left">
                      {item.title}
                    </h3>
                    <p className="text-xs text-gray-400 font-normal leading-relaxed text-left">
                      {item.description}
                    </p>
                  </div>
                </div>

                {/* Right block: Action Status Switches & Add Photo Trigger */}
                <div className="flex flex-nowrap items-center gap-3 self-end md:self-start flex-shrink-0 whitespace-nowrap">
                  {item.subItems.length > 0 && <SelectAllButton section={item.title}
                    disabled={item.subItems.every(subItem => subItem.checked)}
                    onClick={() => setChecklist(previous => previous.map(section => section.id === item.id ? { ...section, isExpanded: true, subItems: section.subItems.map(subItem => ({ ...subItem, checked: true })) } : section))} />}
                  {/* Pass Status Button */}
                  <button
                    onClick={() => handleStatusChange(item.id, 'pass')}
                    className={`flex items-center space-x-1.5 px-4 py-2 border rounded-xl text-xs font-bold transition shadow-sm ${
                      item.status === 'pass'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <FiCheckCircle className={`w-4 h-4 ${item.status === 'pass' ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <span>Pass</span>
                  </button>

                  {/* Issue Status Button */}
                  <button
                    onClick={() => handleStatusChange(item.id, 'issue')}
                    className={`flex items-center space-x-1.5 px-4 py-2 border rounded-xl text-xs font-bold transition shadow-sm ${
                      item.status === 'issue'
                        ? 'bg-red-50 border-red-200 text-red-700'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <FiAlertCircle className={`w-4 h-4 ${item.status === 'issue' ? 'text-red-500' : 'text-gray-400'}`} />
                    <span>Issue</span>
                  </button>

                  {/* Accordion Chevron Trigger Toggle */}
                  <button
                    onClick={() => toggleExpand(item.id)}
                    className="p-1.5 rounded-lg border border-gray-100 hover:bg-gray-50 text-gray-400 transition"
                  >
                    {item.isExpanded ? <BiChevronUp className="w-5 h-5" /> : <BiChevronDown className="w-5 h-5" />}
                  </button>
                </div>

              </div>

              {/* Accordion Content Panel (List of Nested Checks) */}
              {item.isExpanded && (
                <div className="mt-5 ml-0 md:ml-14 pl-0 md:pl-2 border-l-0 md:border-l-2 md:border-gray-50 space-y-4 animate-fadeIn">
                  
                  {/* Sub-item Checklist Grid */}
                  <div className="space-y-3.5">
                    {item.subItems.map((subItem) => (
                      <div key={subItem.id} className="space-y-1.5">
                        <div 
                          onClick={() => toggleSubItemCheck(item.id, subItem.id)}
                          className="flex items-start space-x-3 cursor-pointer group w-fit max-w-full"
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {subItem.checked ? (
                              <FiCheckSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                              <FiSquare className="w-4 h-4 text-gray-300 group-hover:text-gray-400 transition" />
                            )}
                          </div>
                          <span className="text-xs font-semibold text-gray-700 text-left leading-relaxed">
                            {subItem.text}
                          </span>
                        </div>

                        {/* Optional Inline Inner Context Information Example Row */}
                        {subItem.exampleText && (
                          <div className="ml-7 p-2.5 bg-blue-50/50 border border-blue-100 rounded-xl w-fit text-left">
                            <p className="text-[11px] font-medium text-blue-700 italic leading-snug">
                              {subItem.exampleText}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Upload Secondary Button Tool Block */}
                  <div className="pt-2">
                    <button className="flex items-center space-x-1.5 px-3 py-1.5 border border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50/20 rounded-lg text-[11px] font-bold text-gray-500 hover:text-blue-600 transition shadow-sm">
                      <FiCamera className="w-3.5 h-3.5" />
                      <span>Add photo (optional)</span>
                    </button>
                  </div>

                </div>
              )}

            </div>
          );
        })}
      </div>
    </div>
  );
}
